import { BUTTON_TYPES, DSButtonV2 } from '@elliemae/ds-button';
import { ChevronSmallDown } from '@elliemae/ds-icons';
import { DSMenuButton } from '@elliemae/ds-menu-button';
import { DSTooltipV3, tooltipPositions } from '@elliemae/ds-tooltip';
import { memo } from 'react';
import { StyledMIButtonWrapper, StyledMIMenuButtonWrapper } from './styled';
import { MILoadingSkeleton } from './MILoadingSkeleton';

export const MI_NOT_APPLICABLE_TOOLTIP =
  'MI is not applicable for this program';
export const MI_ALREADY_ORDERED_TOOLTIP = 'MI has already been ordered';

interface GetMIButtonProps {
  isDisabled: boolean;
  isLoading?: boolean;
  disabledTooltip?: string;
  isError?: boolean;
  errorTooltip?: string;
  // Single-type: provide onClick
  onClick?: () => void;
  // Multi-type: provide options + onSelectMiType
  options?: { dsId: string; type: string; label: string; value: string }[];
  onSelectMiType?: (miType: string) => void;
}

export const GetMIButton = memo(function GetMIButton({
  isDisabled,
  isLoading = false,
  onClick,
  disabledTooltip = MI_NOT_APPLICABLE_TOOLTIP,
  isError = false,
  errorTooltip,
  options,
  onSelectMiType,
}: GetMIButtonProps) {
  if (isLoading) {
    return (
      <StyledMIButtonWrapper
        onClick={(e) => e.stopPropagation()}
        data-testid="get-mi-button-loading"
      >
        <MILoadingSkeleton />
      </StyledMIButtonWrapper>
    );
  }

  const label = isError ? 'MI Error' : 'Get MI';

  if (options && onSelectMiType) {
    const menuButton = (
      <DSMenuButton
        options={options}
        onActivateItem={({ plainItem: { value } }: any) =>
          onSelectMiType(value)
        }
        buttonType="outline"
        size="s"
        disabled={isDisabled}
        data-testid="get-mi"
      >
        {label}
        <ChevronSmallDown size="s" style={{ marginLeft: '4px' }} />
      </DSMenuButton>
    );
    if (isError && errorTooltip) {
      return (
        <StyledMIMenuButtonWrapper onClick={(e) => e.stopPropagation()}>
          <DSTooltipV3
            startPlacementPreference={tooltipPositions.TOP}
            text={errorTooltip}
            withoutPortal={false}
            wrapWords
          >
            {menuButton}
          </DSTooltipV3>
        </StyledMIMenuButtonWrapper>
      );
    }
    return (
      <StyledMIMenuButtonWrapper onClick={(e) => e.stopPropagation()}>
        {isDisabled ? (
          <DSTooltipV3
            startPlacementPreference={tooltipPositions.TOP}
            text={disabledTooltip}
            withoutPortal={false}
          >
            {menuButton}
          </DSTooltipV3>
        ) : (
          menuButton
        )}
      </StyledMIMenuButtonWrapper>
    );
  }

  const button = (
    <DSButtonV2
      buttonType={BUTTON_TYPES.OUTLINE}
      disabled={isDisabled}
      onClick={!isDisabled ? onClick : undefined}
      data-testid="get-mi-button"
      size="s"
    >
      {label}
    </DSButtonV2>
  );

  if (isError && errorTooltip) {
    return (
      <StyledMIButtonWrapper
        onClick={(e) => e.stopPropagation()}
        isError={isError}
      >
        <DSTooltipV3
          startPlacementPreference={tooltipPositions.TOP}
          text={errorTooltip}
          withoutPortal={false}
          wrapWords
        >
          {button}
        </DSTooltipV3>
      </StyledMIButtonWrapper>
    );
  }

  return (
    <StyledMIButtonWrapper onClick={(e) => e.stopPropagation()}>
      {isDisabled ? (
        <DSTooltipV3
          startPlacementPreference={tooltipPositions.TOP}
          text={disabledTooltip}
          withoutPortal={false}
        >
          {button}
        </DSTooltipV3>
      ) : (
        button
      )}
    </StyledMIButtonWrapper>
  );
});
